import { useState, useEffect } from 'react';
import { ApiFout, api } from '@/lib/api';
import type { AppToken, BeheerGebruiker, BeheerPraktijk } from '@/lib/api-types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { UserCostsTab } from '@/components/settings/UserCostsTab';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Plus, Pencil, Trash2, Copy, RefreshCw, Settings2, Globe, Euro, Users, Wrench, ExternalLink, Phone, ToggleLeft, ToggleRight } from 'lucide-react';
import { toast } from 'sonner';
import { AiCostsTab } from '@/components/settings/AiCostsTab';
import { AZURE_SPEECH_LANGUAGES, DEFAULT_SPEECH_LANGUAGE } from '@/lib/azure-languages';

/**
 * Een praktijk zoals dit scherm hem kent.
 *
 * Let op wat er niet in staat: access_token. Dat kwam in de bron mee met elke praktijk
 * in elke lijst, dus stond het in het netwerkverkeer en in de DOM van iedereen die dit
 * overzicht opende. Wie het nodig heeft vraagt het per praktijk op met
 * GET /admin/practices/:id/access-token, en die aanvraag staat in de logs.
 *
 * sip_password staat er ook niet in; sipPasswordSet zegt of er een staat.
 */
type Practice = BeheerPraktijk;

export function PracticesTab() {
  const [practices, setPractices] = useState<Practice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [appToken, setAppToken] = useState('');
  const [appTokens, setAppTokens] = useState<AppToken[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPractice, setEditingPractice] = useState<Practice | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Practice | null>(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  /**
   * Of gedeactiveerde praktijken meedoen in het overzicht. Standaard niet: een
   * praktijk wordt gedeactiveerd omdat er niet meer mee gewerkt wordt, en dan hoort
   * hij niet tussen de praktijken te staan waar dat wel gebeurt.
   */
  const [toonInactief, setToonInactief] = useState(false);

  // Practice form
  const [practiceName, setPracticeName] = useState('');
  const [agbCode, setAgbCode] = useState('');
  const [afasId, setAfasId] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Practice users state
  const [practiceUsers, setPracticeUsers] = useState<BeheerGebruiker[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  // User settings dialog
  const [userSettingsOpen, setUserSettingsOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<typeof practiceUsers[0] | null>(null);
  const [userDisplayName, setUserDisplayName] = useState('');
  const [userAutoTranslate, setUserAutoTranslate] = useState<boolean | null>(null);
  const [userAutoSuggestIcpc, setUserAutoSuggestIcpc] = useState<boolean | null>(null);
  const [userDoctorPhrase, setUserDoctorPhrase] = useState<string | null>(null);
  const [userReadTriggerPhrase, setUserReadTriggerPhrase] = useState<string | null>(null);
  const [userSipHost, setUserSipHost] = useState<string | null>(null);
  const [userSipWsPort, setUserSipWsPort] = useState<string | null>(null);
  const [userSipWsPath, setUserSipWsPath] = useState<string | null>(null);
  const [userSipExtension, setUserSipExtension] = useState<string | null>(null);
  const [userSipPassword, setUserSipPassword] = useState<string | null>(null);
  const [userSipDisplayName, setUserSipDisplayName] = useState<string | null>(null);
  const [userSipStunServer, setUserSipStunServer] = useState<string | null>(null);
  const [userSipDialPrefix, setUserSipDialPrefix] = useState<string | null>(null);
  const [isSavingUser, setIsSavingUser] = useState(false);

  // Settings dialog
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsPractice, setSettingsPractice] = useState<Practice | null>(null);
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [isSavingSettings, setIsSavingSettings] = useState(false);

  // Settings form state
  const [autoTranslate, setAutoTranslate] = useState(true);
  const [autoSuggestIcpc, setAutoSuggestIcpc] = useState(true);
  const [callingEnabled, setCallingEnabled] = useState(false);
  const [patientShareEnabled, setPatientShareEnabled] = useState(false);
  const [liveTranscriptEnabled, setLiveTranscriptEnabled] = useState(false);
  const [localApiUrl, setLocalApiUrl] = useState('http://localhost:8080/soep');
  const [defaultLanguages, setDefaultLanguages] = useState<string[]>([DEFAULT_SPEECH_LANGUAGE.code]);
  // SIP trunk per praktijk
  const [sipHost, setSipHost] = useState('');
  const [sipWsPort, setSipWsPort] = useState('');
  const [sipWsPath, setSipWsPath] = useState('/ws');
  const [sipExtension, setSipExtension] = useState('');
  const [sipPassword, setSipPassword] = useState('');
  const [sipDisplayName, setSipDisplayName] = useState('');
  const [sipStunServer, setSipStunServer] = useState('');
  const [sipDialPrefix, setSipDialPrefix] = useState('');


  const fetchAppToken = async () => {
    try {
      const { tokens } = await api.get<{ tokens: AppToken[] }>('/admin/app-tokens');
      const actief = tokens.filter((t) => t.isActive);
      setAppTokens(actief);
      if (actief.length > 0) setAppToken(actief[0].appToken);
    } catch {
      // Zonder tokens kan er geen HIS-URL worden samengesteld; het scherm blijft
      // verder bruikbaar, dus geen melding.
      setAppTokens([]);
    }
  };

  const fetchPractices = async () => {
    setIsLoading(true);
    try {
      const { practices } = await api.get<{ practices: BeheerPraktijk[] }>('/admin/practices');
      setPractices(practices);
    } catch (fout) {
      toast.error(fout instanceof ApiFout ? fout.message : 'Kon praktijken niet laden');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void fetchPractices();
    void fetchAppToken();
  }, []);

  const resetForm = () => {
    setPracticeName('');
    setAgbCode('');
    setAfasId('');
    setEditingPractice(null);
  };

  const openNew = () => { resetForm(); setDialogOpen(true); };

  const openEdit = (p: Practice) => {
    setEditingPractice(p);
    setPracticeName(p.practiceName);
    setAgbCode(p.agbCode);
    setAfasId(p.afasId || '');
    setDialogOpen(true);
  };

  const openSettings = (p: Practice) => {
    setSettingsPractice(p);
    setAutoTranslate(p.autoTranslate ?? true);
    setAutoSuggestIcpc(p.autoSuggestIcpc ?? true);
    setCallingEnabled(p.callingEnabled ?? false);
    setPatientShareEnabled(p.patientShareEnabled ?? false);
    setLiveTranscriptEnabled(p.liveTranscriptEnabled ?? false);
    setLocalApiUrl(p.localApiUrl ?? 'http://localhost:8080/soep');
    setDefaultLanguages(p.defaultLanguages ?? [DEFAULT_SPEECH_LANGUAGE.code]);
    setSipHost(p.sipHost ?? '');
    setSipWsPort(p.sipWsPort === '8089' ? '' : (p.sipWsPort ?? ''));
    setSipWsPath(p.sipWsPath ?? '/ws');
    setSipExtension(p.sipExtension ?? '');
    setSipPassword('');
    setSipDisplayName(p.sipDisplayName ?? '');
    setSipStunServer(p.sipStunServer ?? '');
    setSipDialPrefix(p.sipDialPrefix ?? '');
    setSettingsOpen(true);
    fetchPracticeUsers(p.id);
  };

  const fetchPracticeUsers = async (practiceId: string) => {
    setIsLoadingUsers(true);
    try {
      const { users } = await api.get<{ users: BeheerGebruiker[] }>('/admin/practice-users', {
        query: { practiceId },
      });
      setPracticeUsers(users);
    } catch (fout) {
      toast.error(fout instanceof ApiFout ? fout.message : 'Kon de gebruikers niet laden');
      setPracticeUsers([]);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const openUserSettings = (u: typeof practiceUsers[0]) => {
    setEditingUser(u);
    setUserDisplayName(u.displayName || '');
    setUserAutoTranslate(u.autoTranslate);
    setUserAutoSuggestIcpc(u.autoSuggestIcpc);
    setUserDoctorPhrase(u.doctorPhrase);
    setUserReadTriggerPhrase(u.readTriggerPhrase);
    setUserSipHost(u.sipHost);
    setUserSipWsPort(u.sipWsPort === '8089' ? null : u.sipWsPort);
    setUserSipWsPath(u.sipWsPath);
    setUserSipExtension(u.sipExtension);
    setUserSipPassword(null);
    setUserSipDisplayName(u.sipDisplayName);
    setUserSipStunServer(u.sipStunServer);
    setUserSipDialPrefix(u.sipDialPrefix);
    setUserSettingsOpen(true);
  };

  const handleSaveUserSettings = async () => {
    if (!editingUser || !settingsPractice) return;
    setIsSavingUser(true);
    try {
      await api.patch(`/admin/practice-users/${editingUser.id}`, {
        displayName: userDisplayName.trim() || null,
        autoTranslate: userAutoTranslate,
        autoSuggestIcpc: userAutoSuggestIcpc,
        doctorPhrase: userDoctorPhrase,
        readTriggerPhrase: userReadTriggerPhrase,
        sipHost: userSipHost,
        // 8089 is de standaardpoort; die hoort niet in de websocket-URL terecht te
        // komen, dus leeg betekent hier "standaard".
        sipWsPort: userSipWsPort === '8089' ? null : userSipWsPort,
        sipWsPath: userSipWsPath,
        sipExtension: userSipExtension,
        sipPassword: userSipPassword,
        sipDisplayName: userSipDisplayName,
        sipStunServer: userSipStunServer,
        sipDialPrefix: userSipDialPrefix,
      });
      toast.success('Gebruikersinstellingen opgeslagen');
      setUserSettingsOpen(false);
      await fetchPracticeUsers(settingsPractice.id);
    } catch (fout) {
      toast.error(fout instanceof ApiFout ? fout.message : 'Fout bij opslaan');
    } finally {
      setIsSavingUser(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!settingsPractice) return;
    try {
      // De route weigert een gebruiker met verbruiksregels: die hangen met
      // on delete cascade aan practice_users, dus verwijderen zou de facturering van
      // een al afgesloten maand wijzigen.
      await api.delete(`/admin/practice-users/${userId}`);
      toast.success('Gebruiker verwijderd');
      await fetchPracticeUsers(settingsPractice.id);
    } catch (fout) {
      toast.error(fout instanceof ApiFout ? fout.message : 'Kon gebruiker niet verwijderen');
    }
  };

  const handleToggleUserActive = async (userId: string, currentActive: boolean) => {
    if (!settingsPractice) return;
    try {
      await api.patch(`/admin/practice-users/${userId}`, { isActive: !currentActive });
      await fetchPracticeUsers(settingsPractice.id);
    } catch (fout) {
      toast.error(fout instanceof ApiFout ? fout.message : 'Fout bij bijwerken');
    }
  };

  const handleSave = async () => {
    if (!practiceName.trim() || !agbCode.trim()) {
      toast.error('Praktijknaam en AGB-code zijn verplicht');
      return;
    }
    setIsSaving(true);
    try {
      const velden = {
        practiceName: practiceName.trim(),
        agbCode: agbCode.trim(),
        afasId: afasId.trim() || null,
      };

      if (editingPractice) {
        await api.patch(`/admin/practices/${editingPractice.id}`, velden);
        toast.success('Praktijk bijgewerkt');
      } else {
        await api.post('/admin/practices', velden);
        toast.success('Praktijk aangemaakt');
      }

      setDialogOpen(false);
      resetForm();
      await fetchPractices();
    } catch (fout) {
      toast.error(fout instanceof ApiFout ? fout.message : 'Fout bij opslaan');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!settingsPractice) return;
    setIsSavingSettings(true);
    try {
      await api.patch(`/admin/practices/${settingsPractice.id}`, {
        autoTranslate,
        autoSuggestIcpc,
        callingEnabled,
        patientShareEnabled,
        liveTranscriptEnabled,
        localApiUrl: localApiUrl.trim(),
        defaultLanguages,
        sipHost: sipHost.trim() || null,
        sipWsPort: (sipWsPort.trim() === '8089' ? '' : sipWsPort.trim()) || null,
        sipWsPath: sipWsPath.trim() || null,
        // Extensie en wachtwoord horen bij een persoon en niet bij een praktijk;
        // dit formulier zet ze daarom leeg. Per gebruiker gaat het via de
        // gebruikersinstellingen.
        sipExtension: null,
        sipPassword: null,
        sipDisplayName: sipDisplayName.trim() || null,
        sipStunServer: sipStunServer.trim() || null,
        sipDialPrefix: sipDialPrefix.trim() || null,
      });
      toast.success('Instellingen opgeslagen');
      await fetchPractices();
    } catch (fout) {
      toast.error(fout instanceof ApiFout ? fout.message : 'Fout bij opslaan instellingen');
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      // De route weigert een praktijk waar verbruik op staat. Niet omdat die regels
      // dan meegaan -- ze blijven juist staan -- maar omdat ze hun gebruiker en
      // daarmee hun praktijk kwijtraken, en zo uit de facturering vallen.
      // Deactiveren is dan de weg. Gebruikers zonder verbruik gaan wel mee.
      const antwoord = await api.delete<{ gebruikers?: number }>(`/admin/practices/${id}`);
      toast.success(
        antwoord?.gebruikers
          ? `Praktijk verwijderd, met ${antwoord.gebruikers} gebruiker(s)`
          : 'Praktijk verwijderd',
      );
      await fetchPractices();
    } catch (fout) {
      toast.error(fout instanceof ApiFout ? fout.message : 'Kon praktijk niet verwijderen');
    } finally {
      setDeleteTarget(null);
      setDeleteConfirmName('');
    }
  };

  /**
   * Een praktijk deactiveren of weer aanzetten.
   *
   * Dezelfde weg als bij een app-token: één PATCH op is_active, zonder tussenvraag.
   * Het effect is wel onmiddellijk en het reikt verder dan een nieuwe aanmelding --
   * requirePractice leest is_active bij elk verzoek, dus ook wie de app al open heeft
   * staan komt er bij de volgende aanroep niet meer langs. Vandaar dat de melding
   * zegt wat er gebeurt in plaats van alleen "opgeslagen".
   */
  const handleToggleActive = async (p: Practice) => {
    try {
      await api.patch(`/admin/practices/${p.id}`, { isActive: !p.isActive });
      toast.success(
        p.isActive
          ? `${p.practiceName} is gedeactiveerd; gebruikers kunnen de app niet meer openen`
          : `${p.practiceName} is weer actief`,
      );
      await fetchPractices();
    } catch (fout) {
      toast.error(fout instanceof ApiFout ? fout.message : 'Kon de status niet wijzigen');
    }
  };

  const handleRegenerateToken = async (id: string) => {
    if (
      !confirm(
        'Het toegangstoken vernieuwen? Deze praktijk kan de app niet meer openen tot het nieuwe token in het HIS staat.',
      )
    ) {
      return;
    }

    try {
      /**
       * De server genereert het token, niet de browser.
       *
       * In de bron deed dit scherm `crypto.randomUUID()` en schreef de uitkomst
       * rechtstreeks naar de kolom. Het verschil is niet de kwaliteit van de UUID maar
       * dat de client de waarde niet meer kiest -- en dat access_token nergens anders
       * in een antwoord staat, dus dit is het enige moment waarop hij te lezen is.
       */
      const antwoord = await api.post<{ accessToken: string; waarschuwing: string }>(
        `/admin/practices/${id}/rotate-token`,
      );

      await navigator.clipboard.writeText(antwoord.accessToken).catch(() => {
        /* zonder klembordrechten blijft de melding het enige aanbod */
      });

      toast.success('Toegangstoken vernieuwd en gekopieerd', {
        description: antwoord.waarschuwing,
        duration: 10_000,
      });
      await fetchPractices();
    } catch (fout) {
      toast.error(fout instanceof ApiFout ? fout.message : 'Kon token niet vernieuwen');
    }
  };

  /**
   * Tokens die deze sessie zijn opgevraagd, per praktijk-id.
   *
   * Ze staan niet in de lijst en worden ook niet bewaard: bij het herladen van de
   * pagina is de map leeg en moet iemand opnieuw vragen. Dat is het punt -- elke
   * aanvraag komt in de serverlogs terecht.
   */
  const [opgevraagdeTokens, setOpgevraagdeTokens] = useState<Record<string, string>>({});

  const haalToken = async (practiceId: string): Promise<string | null> => {
    const bekend = opgevraagdeTokens[practiceId];
    if (bekend) return bekend;

    try {
      const { accessToken } = await api.get<{ accessToken: string }>(
        `/admin/practices/${practiceId}/access-token`,
      );
      setOpgevraagdeTokens((vorige) => ({ ...vorige, [practiceId]: accessToken }));
      return accessToken;
    } catch (fout) {
      toast.error(fout instanceof ApiFout ? fout.message : 'Kon het token niet ophalen');
      return null;
    }
  };

  const copyToken = async (practiceId: string) => {
    const token = await haalToken(practiceId);
    if (!token) return;
    await navigator.clipboard.writeText(token);
    toast.success('Token gekopieerd');
  };

  /** De URL waarmee het HIS de app voor deze gebruiker opent. */
  const bouwHisUrl = async (practiceId: string, externalUserId: string): Promise<string | null> => {
    const token = await haalToken(practiceId);
    if (!token) return null;

    return `${window.location.origin}/soep?token=${token}&user_id=${externalUserId}${
      appToken ? `&app_token=${appToken}` : ''
    }`;
  };

  const kopieerHisUrl = async (practiceId: string, externalUserId: string) => {
    const url = await bouwHisUrl(practiceId, externalUserId);
    if (!url) return;
    await navigator.clipboard.writeText(url);
    toast.success('Preview URL gekopieerd');
  };

  if (isLoading) {
    return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  const aantalInactief = practices.filter((p) => !p.isActive).length;
  const zichtbaar = toonInactief ? practices : practices.filter((p) => p.isActive);

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Praktijken</CardTitle>
            <CardDescription>Beheer praktijken, toegangstokens en instellingen</CardDescription>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Switch
                id="toon-inactief"
                checked={toonInactief}
                onCheckedChange={setToonInactief}
              />
              <Label htmlFor="toon-inactief" className="text-xs cursor-pointer whitespace-nowrap">
                Ook gedeactiveerde
                {aantalInactief > 0 && (
                  <span className="text-muted-foreground"> ({aantalInactief})</span>
                )}
              </Label>
            </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button size="sm" onClick={openNew}><Plus className="h-4 w-4 mr-1" />Nieuw</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingPractice ? 'Praktijk bewerken' : 'Nieuwe praktijk'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <div className="space-y-2">
                  <Label>Praktijknaam *</Label>
                  <Input value={practiceName} onChange={(e) => setPracticeName(e.target.value)} placeholder="Huisartsenpraktijk ..." />
                </div>
                <div className="space-y-2">
                  <Label>AGB-code *</Label>
                  <Input value={agbCode} onChange={(e) => setAgbCode(e.target.value)} placeholder="12345678" />
                </div>
                <div className="space-y-2">
                  <Label>AFAS ID</Label>
                  <Input value={afasId} onChange={(e) => setAfasId(e.target.value)} placeholder="Optioneel" />
                </div>
                {editingPractice && (
                  <div className="space-y-2">
                    <Label>Toegangstoken</Label>
                    <div className="flex gap-2">
                      <Input
                        value={opgevraagdeTokens[editingPractice.id] ?? '••••••••-••••-••••-••••-••••••••••••'}
                        disabled
                        className="bg-muted font-mono text-xs"
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        title="Token ophalen en kopiëren"
                        onClick={() => void copyToken(editingPractice.id)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
                <Button onClick={handleSave} className="w-full" disabled={isSaving}>
                  {isSaving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Opslaan...</> : 'Opslaan'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {zichtbaar.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              {practices.length === 0
                ? 'Nog geen praktijken aangemaakt.'
                : 'Geen actieve praktijken. Zet "Ook gedeactiveerde" aan om ze te zien.'}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Praktijknaam</TableHead>
                  <TableHead>AGB-code</TableHead>
                  <TableHead>AFAS ID</TableHead>
                  <TableHead>Toegangstoken</TableHead>
                  <TableHead className="w-[170px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {zichtbaar.map((p) => (
                  // Gedempt en met het woord erbij: alleen de kleur zou het verschil
                  // dragen, en die is bij een tabelrij te makkelijk te missen.
                  <TableRow key={p.id} className={p.isActive ? undefined : 'opacity-60'}>
                    <TableCell className="font-medium">
                      {p.practiceName}
                      {!p.isActive && (
                        <span className="ml-2 text-xs font-normal text-muted-foreground">
                          gedeactiveerd
                        </span>
                      )}
                    </TableCell>
                    <TableCell>{p.agbCode}</TableCell>
                    <TableCell>{p.afasId || '—'}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <code className="text-xs bg-muted px-1.5 py-0.5 rounded max-w-[180px] truncate block">
                          {opgevraagdeTokens[p.id] ?? '••••••••-••••-••••'}
                        </code>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          title="Token ophalen en kopiëren"
                          onClick={() => void copyToken(p.id)}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleRegenerateToken(p.id)}><RefreshCw className="h-3 w-3" /></Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openSettings(p)} title="Instellingen">
                          <Settings2 className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(p)} title="Bewerken">
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => void handleToggleActive(p)}
                          title={p.isActive ? 'Deactiveren' : 'Activeren'}
                        >
                          {p.isActive
                            ? <ToggleRight className="h-3.5 w-3.5 text-green-600" />
                            : <ToggleLeft className="h-3.5 w-3.5" />}
                        </Button>
                        {p.practiceName !== 'Intern Admin' && (
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => { setDeleteTarget(p); setDeleteConfirmName(''); }} title="Verwijderen">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings2 className="h-5 w-5" />
              Instellingen — {settingsPractice?.practiceName}
            </DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="settings" className="mt-2">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="settings" className="flex items-center gap-1.5">
                <Wrench className="h-4 w-4" />
                Instellingen
              </TabsTrigger>
              <TabsTrigger value="users" className="flex items-center gap-1.5">
                <Users className="h-4 w-4" />
                Gebruikers
              </TabsTrigger>
              <TabsTrigger value="sip" className="flex items-center gap-1.5">
                <Phone className="h-4 w-4" />
                SIP
              </TabsTrigger>
              <TabsTrigger value="costs" className="flex items-center gap-1.5">
                <Euro className="h-4 w-4" />
                Kosten & Omzet
              </TabsTrigger>
            </TabsList>

            {/* Settings Tab (previously AI tab - now only practice-level settings) */}
            <TabsContent value="settings" className="space-y-5 mt-4">
              <div className="space-y-2">
                <Label>Lokale API URL</Label>
                <Input value={localApiUrl} onChange={(e) => setLocalApiUrl(e.target.value)} placeholder="http://localhost:8080/soep" />
                <p className="text-xs text-muted-foreground">URL voor het posten van SOEP-notities</p>
              </div>

              <div className="space-y-2">
                <Label>Standaard talen</Label>
                <Select
                  value={defaultLanguages[0] || 'nl-NL'}
                  onValueChange={(value) => setDefaultLanguages([value])}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {AZURE_SPEECH_LANGUAGES.map((lang) => (
                      <SelectItem key={lang.code} value={lang.code}>{lang.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Automatisch vertalen</Label>
                  <p className="text-xs text-muted-foreground">Vertaal anderstalige tekst automatisch</p>
                </div>
                <Switch checked={autoTranslate} onCheckedChange={setAutoTranslate} />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>ICPC-suggesties</Label>
                  <p className="text-xs text-muted-foreground">Automatisch ICPC-codes voorstellen</p>
                </div>
                <Switch checked={autoSuggestIcpc} onCheckedChange={setAutoSuggestIcpc} />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Bellen toestaan</Label>
                  <p className="text-xs text-muted-foreground">Uitgaand bellen via SIP (WebRTC/FreePBX) in/uitschakelen</p>
                </div>
                <Switch checked={callingEnabled} onCheckedChange={setCallingEnabled} />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Deel met patiënt (QR)</Label>
                  <p className="text-xs text-muted-foreground">Toon de QR-knop in het opnamescherm waarmee de patiënt een PDF-samenvatting kan downloaden</p>
                </div>
                <Switch checked={patientShareEnabled} onCheckedChange={setPatientShareEnabled} />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Live transcriptie (QR)</Label>
                  <p className="text-xs text-muted-foreground">Toon een QR-knop in het opnamescherm waarmee de patiënt het gesprek live op de mobiel kan meelezen (handig voor doven/slechthorenden)</p>
                </div>
                <Switch checked={liveTranscriptEnabled} onCheckedChange={setLiveTranscriptEnabled} />
              </div>

              <Button onClick={handleSaveSettings} className="w-full" disabled={isSavingSettings}>
                {isSavingSettings ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Opslaan...</> : 'Instellingen Opslaan'}
              </Button>
            </TabsContent>




            {/* Users Tab */}
            <TabsContent value="users" className="mt-4">
              {isLoadingUsers ? (
                <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
              ) : practiceUsers.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">
                  Nog geen gebruikers. Gebruikers worden automatisch aangemaakt wanneer ze het opnamescherm openen met <code className="bg-muted px-1 rounded">?user_id=...</code> in de URL.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Gebruiker ID</TableHead>
                      <TableHead>Weergavenaam</TableHead>
                      <TableHead>Actief</TableHead>
                      <TableHead>Aangemaakt</TableHead>
                     <TableHead className="w-[80px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {practiceUsers.map((u) => (
                      <TableRow key={u.id}>
                        <TableCell className="font-mono text-xs">{u.externalUserId}</TableCell>
                        <TableCell>{u.displayName || '—'}</TableCell>
                        <TableCell>
                          <Switch checked={u.isActive} onCheckedChange={() => handleToggleUserActive(u.id, u.isActive)} />
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {new Date(u.createdAt).toLocaleDateString('nl-NL')}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => {
                              void kopieerHisUrl(settingsPractice?.id ?? '', u.externalUserId);
                            }} title="Kopieer preview URL">
                              <Copy className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => {
                              // Het token wordt hier opgehaald en niet uit de lijst
                              // gelezen; vandaar de await voordat het venster opent.
                              void (async () => {
                                const url = await bouwHisUrl(
                                  settingsPractice?.id ?? '',
                                  u.externalUserId,
                                );
                                if (!url) return;
                                const w = settingsPractice?.recordWindowWidth || 800;
                                const h = settingsPractice?.recordWindowHeight || 600;
                                window.open(
                                  url,
                                  '_blank',
                                  `width=${w},height=${h},menubar=no,toolbar=no,location=no,status=no`,
                                );
                              })();
                            }} title="Open in nieuw venster">
                              <ExternalLink className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openUserSettings(u)} title="Instellingen">
                              <Wrench className="h-3.5 w-3.5" />
                            </Button>
                            {settingsPractice?.practiceName !== 'Intern Admin' && (
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDeleteUser(u.id)} title="Verwijderen">
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>

            {/* Costs Tab */}
            <TabsContent value="costs" className="mt-4">
              <AiCostsTab practiceId={settingsPractice?.id} practiceName={settingsPractice?.practiceName} agbCode={settingsPractice?.agbCode} afasId={settingsPractice?.afasId} />
            </TabsContent>

            {/* SIP Tab — per-praktijk SIP-trunk (default voor alle gebruikers) */}
            <TabsContent value="sip" className="mt-4 space-y-4">
              <p className="text-xs text-muted-foreground">
                SIP-trunk (FreePBX/Asterisk via WSS). Gebruikers van deze praktijk gebruiken deze instellingen, tenzij ze zelf een afwijkende configuratie invullen.
              </p>
              <div className="space-y-2">
                <Label>SIP hostname</Label>
                <Input value={sipHost} onChange={(e) => setSipHost(e.target.value)} placeholder="freepbx.praktijk.nl" />
                <p className="text-xs text-muted-foreground">Publieke hostnaam van de PBX (zonder https:// of pad).</p>
              </div>
              <p className="text-xs text-muted-foreground">
                Extensie en wachtwoord worden per gebruiker ingesteld (tab Gebruikersinstellingen).
              </p>
              <div className="space-y-2">
                <Label>WSS-pad</Label>
                <Input value={sipWsPath} onChange={(e) => setSipWsPath(e.target.value)} placeholder="/ws" />
              </div>
              <div className="space-y-2">
                <Label>STUN-server (optioneel)</Label>
                <Input value={sipStunServer} onChange={(e) => setSipStunServer(e.target.value)} placeholder="stun:stun.l.google.com:19302" />
              </div>
              <div className="space-y-2">
                <Label>Beluitprefix (optioneel)</Label>
                <Input value={sipDialPrefix} onChange={(e) => setSipDialPrefix(e.target.value)} placeholder="bv. 0 of 9" />
                <p className="text-xs text-muted-foreground">Wordt automatisch voor elk gekozen telefoonnummer geplaatst bij uitbellen (bv. "0" voor buitenlijn).</p>
              </div>
              <Button onClick={handleSaveSettings} className="w-full" disabled={isSavingSettings}>
                {isSavingSettings ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Opslaan...</> : 'SIP-instellingen Opslaan'}
              </Button>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* User Settings Dialog */}
      <Dialog open={userSettingsOpen} onOpenChange={setUserSettingsOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5" />
              {editingUser?.displayName || editingUser?.externalUserId}
            </DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="settings" className="mt-2">
            <TabsList className="w-full">
              <TabsTrigger value="settings" className="flex-1">Instellingen</TabsTrigger>
              <TabsTrigger value="sip" className="flex-1">SIP</TabsTrigger>
              <TabsTrigger value="costs" className="flex-1">Kosten</TabsTrigger>
            </TabsList>

            <TabsContent value="settings" className="mt-4">
              <div className="space-y-5">
                <div className="space-y-2">
                  <Label>Weergavenaam</Label>
                  <Input value={userDisplayName} onChange={(e) => setUserDisplayName(e.target.value)} placeholder="Bijv. Dr. Jansen" />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Automatisch vertalen</Label>
                    <p className="text-xs text-muted-foreground">Praktijk: {settingsPractice?.autoTranslate ? 'aan' : 'uit'}</p>
                  </div>
                  <Switch
                    checked={userAutoTranslate ?? settingsPractice?.autoTranslate ?? true}
                    onCheckedChange={setUserAutoTranslate}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>ICPC suggesties</Label>
                    <p className="text-xs text-muted-foreground">Praktijk: {settingsPractice?.autoSuggestIcpc ? 'aan' : 'uit'}</p>
                  </div>
                  <Switch
                    checked={userAutoSuggestIcpc ?? settingsPractice?.autoSuggestIcpc ?? true}
                    onCheckedChange={setUserAutoSuggestIcpc}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Zorgverleners herkenningszin</Label>
                  <Input
                    value={userDoctorPhrase ?? settingsPractice?.doctorPhrase ?? ''}
                    onChange={(e) => setUserDoctorPhrase(e.target.value)}
                    placeholder="Bijv. 'Hier spreekt de arts'"
                  />
                  <p className="text-xs text-muted-foreground">Praktijk: {settingsPractice?.doctorPhrase || '(leeg)'}</p>
                </div>

                <div className="space-y-2">
                  <Label>Vertaal trigger</Label>
                  <Input
                    value={userReadTriggerPhrase ?? settingsPractice?.readTriggerPhrase ?? ''}
                    onChange={(e) => setUserReadTriggerPhrase(e.target.value)}
                    placeholder="Bijv. 'Lees voor'"
                  />
                  <p className="text-xs text-muted-foreground">Praktijk: {settingsPractice?.readTriggerPhrase || '(leeg)'}</p>
                </div>

                <Button onClick={handleSaveUserSettings} className="w-full" disabled={isSavingUser}>
                  {isSavingUser ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Opslaan...</> : 'Opslaan'}
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="sip" className="mt-4 space-y-4">
              <p className="text-xs text-muted-foreground">
                Persoonlijke SIP-account voor deze gebruiker. Leeg laten = praktijkstandaard gebruiken.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Extensie</Label>
                  <Input value={userSipExtension ?? ''} onChange={(e) => setUserSipExtension(e.target.value || null)} placeholder={settingsPractice?.sipExtension || '2001'} />
                </div>
                <div className="space-y-2">
                  <Label>Wachtwoord</Label>
                  <Input type="password" value={userSipPassword ?? ''} onChange={(e) => setUserSipPassword(e.target.value || null)} />
                </div>
              </div>
              <Button onClick={handleSaveUserSettings} className="w-full" disabled={isSavingUser}>
                {isSavingUser ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Opslaan...</> : 'SIP-instellingen Opslaan'}
              </Button>
            </TabsContent>

            <TabsContent value="costs" className="mt-4">
              {editingUser && <UserCostsTab practiceUserId={editingUser.id} />}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) { setDeleteTarget(null); setDeleteConfirmName(''); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Praktijk verwijderen</AlertDialogTitle>
            {/*
              Blokken met een span en niet met een p.

              AlertDialogDescription rendert zelf een <p> (dat doet Radix), en een <p>
              daarin is ongeldige HTML: de browser sluit de buitenste dan zelf,
              waarmee de opmaak van dit blok afhangt van foutherstel in plaats van
              van wat hier staat. React waarschuwde er bij elke render over. Een span
              met `block` doet hetzelfde voor space-y-3 en is wel toegestaan. De Input
              mag hier blijven: een <input> is toegestaan binnen een <p>.
            */}
            <AlertDialogDescription className="space-y-3">
              <span className="block">
                U staat op het punt de praktijk <strong>{deleteTarget?.practiceName}</strong> permanent te
                verwijderen, met de instellingen{' '}
                {deleteTarget?.userCount
                  ? `en ${deleteTarget.userCount === 1 ? 'de gekoppelde gebruiker' : `de ${deleteTarget.userCount} gekoppelde gebruikers`}`
                  : ''}
                . Verwijderen kan alleen zolang er geen verbruik op deze praktijk staat; is dat er wel,
                dan weigert de server en blijft deactiveren de weg.
              </span>
              <span className="block">
                Typ de praktijknaam <strong>{deleteTarget?.practiceName}</strong> ter bevestiging:
              </span>
              <Input
                value={deleteConfirmName}
                onChange={(e) => setDeleteConfirmName(e.target.value)}
                placeholder="Typ hier de praktijknaam"
                className="mt-2"
                autoFocus
              />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteConfirmName !== deleteTarget?.practiceName}
              onClick={() => deleteTarget && handleDelete(deleteTarget.id)}
            >
              Definitief verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
